import axios from 'axios';
import { config } from '../config/environment';

export enum OAuthProvider {
  GOOGLE = 'google',
  KAKAO = 'kakao',
  NAVER = 'naver',
}

export interface OAuthConfig {
  provider: OAuthProvider;
  clientId: string;
  clientSecret?: string;
  redirectUri?: string;
}

export interface OAuthResponse {
  success: boolean;
  token?: string;
  user?: {
    id: string | number; // UUID (users 테이블의 id)
    email: string;
    name: string;
    picture?: string;
    provider: OAuthProvider;
  };
  error?: string;
}

class OAuthService {
  private apiUrl = config.apiUrl;

  /**
   * OAuth 로그인 요청
   */
  async login(provider: OAuthProvider, credential: string): Promise<OAuthResponse> {
    try {
      const response = await axios.post<OAuthResponse>(
        `${this.apiUrl}/api/auth/oauth-login`,
        {
          provider,
          credential,
        }
      );

      if (response.data.success) {
        // save-user 결과 확인
        if (!response.data.user || !response.data.user.id) {
          throw new Error('User save failed: Invalid user data from server');
        }

        // 토큰 저장
        if (response.data.token) {
          localStorage.setItem('authToken', response.data.token);
        }
        
        // 사용자 정보 저장 (userId는 users 테이블의 UUID id)
        const userData = {
          ...response.data.user,
          userId: response.data.user.id, // UUID를 userId로도 저장
        };
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('userId', String(response.data.user.id)); // userId 별도 저장
      } else {
        throw new Error('Login failed: ' + (response.data.error || 'Unknown error'));
      }

      return response.data;
    } catch (error) {
      console.error(`${provider} login failed:`, error);
      throw error;
    }
  }

  /**
   * Google 로그인
   */
  async loginWithGoogle(credential: string): Promise<OAuthResponse> {
    return this.login(OAuthProvider.GOOGLE, credential);
  }

  /**
   * Kakao 로그인
   */
  async loginWithKakao(authCode: string): Promise<OAuthResponse> {
    return this.login(OAuthProvider.KAKAO, authCode);
  }

  /**
   * Naver 로그인
   */
  async loginWithNaver(authCode: string): Promise<OAuthResponse> {
    return this.login(OAuthProvider.NAVER, authCode);
  }

  /**
   * 로그아웃
   */
  async logout(): Promise<void> {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        await axios.post(
          `${this.apiUrl}/api/auth/logout`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('userId'); // userId 제거
    }
  }

  /**
   * 현재 사용자 정보 조회
   */
  async getCurrentUser() {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return null;
      }

      const response = await axios.get(`${this.apiUrl}/auth/user`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data.user;
    } catch (error) {
      console.error('Get user failed:', error);
      return null;
    }
  }

  /**
   * 토큰 유효성 검증
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('authToken');
  }
  /**
   * 저장된 사용자 정보 조회
   */
  getStoredUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }
}

const oauthServiceInstance = new OAuthService();
export default oauthServiceInstance;
